import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { useApplicationEvent } from '../../../../../../../core/Analytics/useApplicationEvent';
import { ProductDimensionPay } from '../../../../../../../models/product';
import { useSourceAccountProvidedSkipsAis } from '../../../../../hooks/useSourceAccountProvidedSkipsAis/useSourceAccountProvidedSkipsAis';
import { setPromptType as setPromptTypeImport } from '../../../../../../../stores/prompt-type-store';
import { useSelectedAuthenticationOptions } from '../../../../../../hooks/useSelectedAuthenticationOptions/useSelectedAuthenticationOptions';
import { AppState } from '../../../../../../../stores/redux-store';
import { LoadingOverlay } from '../../../../../../../core/screens/LoadingOverlay/LoadingOverlay';
import { ProviderEntity } from '../../../../../../../models/provider';
import { SessionModel } from '../../../../../../../models/session';
import { useConfiguration } from '../../../../../../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import {
  BankIdMachineView as BankIdMachineViewImport,
  BankIdMachineViewProps,
} from '../../../../../../../connectivity/screens/bank-id/BankIdMachineView';
import {
  AuthenticationPromptType,
  AwaitingScenario,
  Credentials,
  CredentialsEntity,
} from '../../../../../../../models/credentials';
import { onSubmitCredentialsPay as onSubmitCredentialsPayConnect } from '../../onSubmitCredentialsPay/onSubmitCredentialsPay';
import { ApplicationEvent } from '../../../../../../../models/analytics';
import { useProduct } from '../../../../../../../components/ApplicationProviders/ProductProvider/ProductProvider';
import { stopPollingCredentials } from '../../../../../../../connectivity/services/credentials';
import { ParentApplicationEvent } from '../../../../../../../core/Analytics/ParentApplicationEventMessenger';
import {
  resetCredentials as resetCredentialsConnect,
  setCredentials as setCredentialsConnect,
} from '../../../../../../../stores/credentials-store';
import { useCredentialsPoller } from '../../../../../../../components/hooks/useCredentialsPoller/useCredentialsPoller';
import {
  DesktopProps,
  MobileProps,
  SubmitCredentialsScreenBankIDDesktop as SubmitCredentialsScreenBankIDDesktopImport,
  SubmitCredentialsScreenBankIDMobile as SubmitCredentialsScreenBankIDMobileImport,
} from './SubmitCredentialsScreenBankID/SubmitCredentialsScreenBankID';

export type SubmitPayCredentialsBankIDProps = PropsFromRedux & {
  awaitingScenario: AwaitingScenario;
  footerDisclaimer: React.ReactNode | undefined;
  provider: ProviderEntity;
  fields: { [key in string]: string };
  onHeaderButtonClick: () => void;
  SubmitCredentialsScreenBankIDMobile?: React.ComponentType<MobileProps>;
  SubmitCredentialsScreenBankIDDesktop?: React.ComponentType<DesktopProps>;
  BankIdMachineView?: React.ComponentType<BankIdMachineViewProps>;
  isMobile?: typeof userAgent.isMobile;
  isPaymentSca: boolean;
  onExecute: VoidFunction | undefined;
  isOneSca?: boolean;
};

type ShouldSkipSubmitCredentialsScreenPayload = {
  provider: ProviderEntity;
  session?: SessionModel;
};

export const getPrefilledInputfields = ({
  provider,
  session,
}: ShouldSkipSubmitCredentialsScreenPayload): undefined | { [key in string]: string } => {
  const { fields } = provider;

  if (fields.length === 0) {
    return {};
  }

  const personalIdentifier = session?.personalIdentifier;

  if (fields.length === 1 && fields[0].name === 'username' && personalIdentifier) {
    return { username: personalIdentifier };
  }

  return undefined;
};

export const SubmitPayCredentialsBankIDPresenter: React.FC<SubmitPayCredentialsBankIDProps> = ({
  promptType,
  setPromptType,
  credentials,
  credentialsLoading,
  userCredentials,
  provider,
  fields,
  setCredentials,
  resetCredentials,
  onSubmitCredentialsPay,
  onHeaderButtonClick,
  awaitingScenario,
  footerDisclaimer,
  providerPaymentConditions,
  SubmitCredentialsScreenBankIDMobile = SubmitCredentialsScreenBankIDMobileImport,
  SubmitCredentialsScreenBankIDDesktop = SubmitCredentialsScreenBankIDDesktopImport,
  BankIdMachineView = BankIdMachineViewImport,
  isPaymentSca,
  isOneSca,
  isMobile = userAgent.isMobile,
  onExecute,
}: SubmitPayCredentialsBankIDProps) => {
  const canSkipAIS = useSourceAccountProvidedSkipsAis();
  const { sendEvent } = useApplicationEvent();
  const { data: configurationData } = useConfiguration();
  const selectedAuthenticationOptions = useSelectedAuthenticationOptions(provider);
  const { productType, productDimension } = useProduct();
  const { signCredentials } = useCredentialsPoller();

  const sendCredentialsAnalytic = () => {
    sendEvent(ApplicationEvent.CREDENTIALS_SUBMITTED, ParentApplicationEvent.CREDENTIALS_SUBMITTED);
  };

  const initiateLocalCredentials = () => {
    setPromptType(AuthenticationPromptType.LOCAL);
    sendCredentialsAnalytic();

    onSubmitCredentialsPay({
      configurationData,
      fields,
      provider,
      userCredentials,
      providerPaymentConditions,
      canSkipAIS,
    });
    onExecute?.();
  };

  const initiateRemoteCredentials = () => {
    const submitCredentialsPayload = {
      credentials,
      configurationData,
      fields,
      provider,
      userCredentials,
      providerPaymentConditions,
      canSkipAIS,
    };
    setPromptType(AuthenticationPromptType.REMOTE);
    sendCredentialsAnalytic();

    onSubmitCredentialsPay({
      ...submitCredentialsPayload,
      credentialsRequestBase: {
        ...selectedAuthenticationOptions,
      },
    });
    onExecute?.();
  };

  /**
   * this is obviously a temporary solution for payments using XState
   * we try to show submit credentials screen when:
   * - we don't have supplemental info
   * - we have SKIP_CONSENT_SCREEN FF and one-sca-pm case
   */
  const isDirectPaymentsXStateEnabled = false;

  const isPaymentRelated =
    (!isPaymentSca || isOneSca) &&
    (productDimension === ProductDimensionPay.BULK || isDirectPaymentsXStateEnabled);
  const desktopOneSca = Credentials.isUpdated(credentials) && !isMobile && isPaymentRelated;
  const mobileOneSca = Credentials.isUpdated(credentials) && isMobile && isPaymentRelated;

  if ((!credentials && !credentialsLoading) || Credentials.hasError(credentials) || desktopOneSca) {
    if (isMobile) {
      return (
        <SubmitCredentialsScreenBankIDMobile
          provider={provider}
          paymentDetails={footerDisclaimer}
          awaitingScenario={awaitingScenario}
          onHeaderButtonClick={onHeaderButtonClick}
          localDeviceAction={initiateLocalCredentials}
          remoteDeviceAction={initiateRemoteCredentials}
        />
      );
    }

    return (
      <SubmitCredentialsScreenBankIDDesktop
        provider={provider}
        onHeaderButtonClick={onHeaderButtonClick}
        remoteDeviceAction={initiateRemoteCredentials}
      />
    );
  }

  if (credentials)
    if (Credentials.isAwaitingMobileBankId(credentials) || mobileOneSca) {
      stopPollingCredentials();
      const onDone = (c: CredentialsEntity) => {
        setCredentials(c);

        signCredentials(c.id);
      };

      return (
        <BankIdMachineView
          isPaymentSca={awaitingScenario === AwaitingScenario.PAYMENT}
          footerDisclaimer={footerDisclaimer}
          onExecute={onExecute}
          credentials={credentials}
          provider={provider}
          productType={productType}
          configurationData={configurationData}
          onDone={onDone}
          onError={setCredentials}
          onBack={resetCredentials}
          forcedPromptType={promptType}
        />
      );
    }

  return <LoadingOverlay />;
};

const mapStateToProps = (state: AppState) => {
  return {
    promptType: state.promptType.type,
    credentials: state.credentials.data,
    credentialsLoading: state.credentials.loading,
    userCredentials: state.userCredentials.data,
    oauthDescription: state.oauth.description,
    providerPaymentConditions: state.selectedProvider.data.paymentConditions,
  };
};

const mapDispatchToProps = {
  setCredentials: setCredentialsConnect,
  resetCredentials: resetCredentialsConnect,
  onSubmitCredentialsPay: onSubmitCredentialsPayConnect,
  setPromptType: setPromptTypeImport,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const SubmitPayCredentialsBankID = connector(SubmitPayCredentialsBankIDPresenter);
